import type { QueryClient } from "@tanstack/react-query"
import { createRootRouteWithContext, Outlet } from "@tanstack/react-router"
import { lazy, Suspense, useEffect } from "react"
import { Devtools } from "@/app/devtools"
import {
	type AccessState,
	authenticateUser,
	showIdentityRejectedPopup,
	syncUserLocale,
} from "@/entities/user"
import { NotFound } from "@/pages/not-found"
import { LangAttributeSync, useLocale } from "@/shared/lib/i18n"
import { BackButtonManager } from "@/shared/ui/back-button-manager"
import { GlassFilter } from "@/shared/ui/glass-filter"

const AnimationOverlay = lazy(() =>
	import("@/shared/ui/animation-overlay").then((m) => ({
		default: m.AnimationOverlay,
	})),
)

interface MyRouterContext {
	queryClient: QueryClient
	userId: number | null
	isAuthenticated: boolean
	accessState: AccessState
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
	beforeLoad: async ({ context, location }) => {
		const { user, ...auth } = await authenticateUser({
			queryClient: context.queryClient,
			locationSearch: location.search,
			pathname: location.pathname,
		})
		syncUserLocale(user)
		return auth
	},
	component: RootLayout,
	notFoundComponent: NotFound,
})

function RootLayout() {
	const locale = useLocale()
	const { accessState } = Route.useRouteContext()
	const isRejected = accessState.kind === "rejected"

	useEffect(() => {
		if (isRejected) void showIdentityRejectedPopup()
	}, [isRejected])

	return (
		<>
			<div className="app-bg" aria-hidden />
			<div key={locale} className="relative z-0 h-full overflow-hidden">
				<Outlet />
			</div>
			<BackButtonManager />
			<GlassFilter />
			<Suspense fallback={null}>
				<AnimationOverlay />
			</Suspense>
			<LangAttributeSync />
			<Devtools />
		</>
	)
}
